/**
 * SupplierDetail — Fiche fournisseur 360° pleine page.
 * Miroir de ClientDetail.jsx.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, FileText, Building2, MapPin, Mail, Phone, Pencil,
  AlertTriangle, TrendingUp, Save, Sparkles, Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SupplierEditorSheet from "@/components/SupplierEditorSheet";

const eur = (n, prec = 0) => `${(Number(n) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: prec, maximumFractionDigits: prec,
})} €`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin",
                  "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const STATUS_META = {
  new:       { label: "🆕 Nouveau",     cls: "bg-sky-50 text-sky-700 border-sky-200" },
  strategic: { label: "🌟 Stratégique", cls: "bg-purple-50 text-purple-700 border-purple-200" },
  active:    { label: "✅ Actif",       cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  dormant:   { label: "💤 Dormant",     cls: "bg-slate-100 text-slate-600 border-slate-200" },
  at_risk:   { label: "⚠️ À risque",    cls: "bg-red-50 text-red-700 border-red-200" },
};

const METHOD_LABELS = {
  bank_transfer:     "Virement",
  direct_debit:      "Prélèvement",
  card:              "Carte bancaire",
  cash:              "Espèces",
  check:             "Chèque",
  other:             "Autre",
  historical_import: "Import historique",
};

const INV_STATUS_META = {
  imported:       { label: "Importée", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  to_validate:    { label: "À valider", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  validated:      { label: "Validée", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  partially_paid: { label: "Part. payée", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  paid:           { label: "Payée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:       { label: "Refusée", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("expenses.write");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/suppliers/${id}/360`);
      setData(r.data);
      setNotes(r.data.supplier?.notes || "");
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app/suppliers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleRecurring = async () => {
    setSaving(true);
    try {
      await api.patch(`/suppliers/${id}`,
        { is_recurring: !data.supplier.is_recurring_manual });
      toast.success("Récurrence mise à jour");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/suppliers/${id}`, { notes });
      toast.success("Notes enregistrées");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="p-8 text-sm text-slate-500">Chargement…</div>;
  }
  const { supplier: sup, kpi, monthly_volume_12m, top_pcg_codes,
          payment_methods_12m, alerts, invoices } = data;
  const status = STATUS_META[sup.status] || STATUS_META.new;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="supplier-detail-page">
      <button onClick={() => navigate("/app/suppliers")}
              className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="supplier-back-btn">
        <ArrowLeft className="h-3.5 w-3.5" /> Retour aux fournisseurs
      </button>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
            Fiche fournisseur 360°
          </div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-semibold text-slate-900 truncate"
              style={{ fontFamily: "Manrope, sans-serif" }}>
            {sup.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={cn("text-[10px]", status.cls)}
                   data-testid="supplier-status-badge">
              {status.label}
            </Badge>
            {sup.is_recurring && (
              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                Récurrent
              </Badge>
            )}
            {sup.concentration_pct > 30 && (
              <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">
                Concentration {sup.concentration_pct}%
              </Badge>
            )}
            {sup.siret && (
              <span className="font-mono text-[11px] text-slate-500">SIRET {sup.siret}</span>
            )}
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditorOpen(true)}
                    data-testid="supplier-edit-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modifier
            </Button>
          </div>
        )}
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => {
            const cls = a.level === "critical" ? "bg-red-50 border-red-200 text-red-800"
                     : a.level === "warning" ? "bg-amber-50 border-amber-200 text-amber-800"
                     : "bg-blue-50 border-blue-200 text-blue-800";
            return (
              <div key={i} className={cn("border rounded-lg p-3 text-sm flex items-start gap-2", cls)}
                   data-testid={`supplier-alert-${a.level}`}>
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs opacity-80 mt-0.5">{a.message}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* KPI 6 tuiles */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Kpi label="Volume 12 m" value={eur(kpi.volume_12m)} accent="blue" />
        <Kpi label="Volume total" value={eur(kpi.volume_total)} accent="slate" />
        <Kpi label="Reste dû" value={eur(kpi.outstanding_ttc)}
             accent={kpi.overdue_ttc > 0 ? "red" : kpi.outstanding_ttc > 0 ? "amber" : "emerald"} />
        <Kpi label="En retard" value={eur(kpi.overdue_ttc)}
             accent={kpi.overdue_ttc > 0 ? "red" : "slate"} />
        <Kpi label="DSO" value={kpi.dso_days === null || kpi.dso_days === undefined ? "—" : `${kpi.dso_days} j`}
             accent={kpi.dso_days > 15 ? "amber" : "slate"} />
        <Kpi label="Panier moy." value={eur(kpi.avg_ticket)} accent="slate" />
      </div>

      {/* 2 colonnes : Graph 12m + Top PCG */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 bg-white border-slate-200 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Volume 12 mois</h3>
              <p className="text-[11px] text-slate-500">TTC par mois</p>
            </div>
            <TrendingUp className="h-4 w-4 text-slate-400" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={monthly_volume_12m}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
              <RTooltip formatter={(v) => eur(v, 2)} labelFormatter={fmtMonth} />
              <Area type="monotone" dataKey="amount" stroke="#2563eb" fill="url(#volGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5 bg-white border-slate-200 rounded-xl">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Top postes PCG</h3>
          {top_pcg_codes.length === 0 ? (
            <p className="text-xs text-slate-500">Aucune donnée.</p>
          ) : (
            <ul className="space-y-2">
              {top_pcg_codes.map((p) => (
                <li key={p.pcg_code} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-slate-700">{p.pcg_code}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">{p.count}</span>
                    <span className="font-mono font-semibold text-blue-700">{eur(p.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {payment_methods_12m.length > 0 && (
            <>
              <h3 className="text-sm font-semibold text-slate-900 mt-5 mb-2 flex items-center gap-1.5">
                <Wallet className="h-3.5 w-3.5" /> Règlements 12 m
              </h3>
              <ul className="space-y-1.5">
                {payment_methods_12m.map((m) => (
                  <li key={m.method} className="flex items-center justify-between text-xs">
                    <span className="text-slate-700">{METHOD_LABELS[m.method] || m.method}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">{m.count}×</span>
                      <span className="font-mono font-semibold">{eur(m.amount)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      {/* Sidebar coordonnées + Historique */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 bg-white border-slate-200 rounded-xl">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Coordonnées</h3>
          <div className="space-y-1.5 text-xs text-slate-700">
            {sup.contact_email && (
              <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-slate-400" />{sup.contact_email}</div>
            )}
            {sup.phone && (
              <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-slate-400" />{sup.phone}</div>
            )}
            {sup.address && (
              <div className="flex items-start gap-2">
                <MapPin className="h-3 w-3 text-slate-400 mt-0.5" />
                <span>{sup.address}{sup.postal_code && `, ${sup.postal_code}`}{sup.city && ` ${sup.city}`}</span>
              </div>
            )}
            {sup.siret && (
              <div className="font-mono text-[11px] text-slate-500">SIRET {sup.siret}</div>
            )}
            {sup.siren && !sup.siret && (
              <div className="font-mono text-[11px] text-slate-500">SIREN {sup.siren}</div>
            )}
            {sup.vat_number && (
              <div className="font-mono text-[11px] text-slate-500">TVA {sup.vat_number}</div>
            )}
          </div>

          {canWrite && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="supplier-detail-recurring"
                       className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                  Fournisseur récurrent
                </label>
                <Switch id="supplier-detail-recurring"
                        checked={!!sup.is_recurring_manual}
                        onCheckedChange={toggleRecurring}
                        disabled={saving}
                        data-testid="supplier-detail-recurring-toggle" />
              </div>
              <p className="text-[10px] text-slate-500">
                Auto : ≥ 3 factures / 12 m ({sup.is_recurring_auto ? "actif" : "non"}).
              </p>

              <div className="mt-3">
                <label htmlFor="supplier-detail-notes"
                       className="text-xs uppercase tracking-wider text-slate-500 font-medium">Notes</label>
                <Textarea id="supplier-detail-notes" value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Notes internes…" className="mt-1 text-xs"
                          rows={3} data-testid="supplier-detail-notes" />
                <Button onClick={saveNotes} disabled={saving} size="sm"
                        className="mt-2 w-full" data-testid="supplier-detail-save-notes-btn">
                  <Save className="h-3.5 w-3.5 mr-1.5" />
                  {saving ? "Enregistrement…" : "Enregistrer notes"}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 p-5 bg-white border-slate-200 rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Historique factures ({invoices.length})
            </h3>
            <Link to={`/app/purchases/supplier-invoices?supplier_id=${sup.id}`}
                  className="text-xs text-blue-600 hover:underline">
              Voir toutes →
            </Link>
          </div>
          {invoices.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">
              Aucune facture pour ce fournisseur.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2">N°</th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Échéance</th>
                    <th className="text-left p-2">Statut</th>
                    <th className="text-right p-2">TTC</th>
                    <th className="text-right p-2">Reste</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const st = INV_STATUS_META[inv.status] || { label: inv.status, cls: "bg-slate-100 text-slate-700 border-slate-200" };
                    const remaining = Math.max(0, (inv.total_ttc || 0) - (inv.paid_amount_ttc || 0));
                    return (
                      <tr key={inv.id} className="border-t border-slate-100 hover:bg-slate-50"
                          data-testid={`supplier-invoice-row-${inv.id}`}>
                        <td className="p-2 font-mono">
                          <Link to={`/app/purchases/supplier-invoices/${inv.id}`}
                                className="text-blue-600 hover:underline">
                            {inv.invoice_number}
                          </Link>
                        </td>
                        <td className="p-2 text-slate-600">{fmtDate(inv.invoice_date)}</td>
                        <td className="p-2 text-slate-600">{fmtDate(inv.due_date)}</td>
                        <td className="p-2">
                          <Badge variant="outline" className={cn("text-[9px]", st.cls)}>
                            {st.label}
                          </Badge>
                        </td>
                        <td className="p-2 text-right font-mono font-medium">{eur(inv.total_ttc)}</td>
                        <td className={cn("p-2 text-right font-mono",
                                          remaining > 0.01 ? "text-amber-700" : "text-slate-400")}>
                          {remaining > 0.01 ? eur(remaining) : "—"}
                        </td>
                        <td className="p-2 text-right">
                          {inv.file_asset_id && (
                            <FileText className="h-3.5 w-3.5 text-slate-400 inline" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <SupplierEditorSheet supplierId={id} open={editorOpen}
                            onOpenChange={setEditorOpen}
                            onSaved={() => load()} />
    </div>
  );
}

function Kpi({ label, value, accent = "slate" }) {
  const cls = {
    blue:    "text-blue-700",
    slate:   "text-slate-900",
    red:     "text-red-700",
    amber:   "text-amber-700",
    emerald: "text-emerald-700",
  }[accent] || "text-slate-900";
  return (
    <Card className="p-3 bg-white border-slate-200 rounded-xl"
          data-testid={`supplier-detail-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">
        {label}
      </div>
      <div className={cn("mt-1 text-lg font-semibold font-mono tabular-nums", cls)}>
        {value}
      </div>
    </Card>
  );
}
